<template>
    <!-- Asset Information Modal -->
    <div class="modal edit-touchpoint-modal" :class="{ 'is-generating': isAssetGenerating }" :id="modalId" tabindex="-1" :aria-labelledby="modalLabelId" aria-hidden="true">
        <!-- Add scrollable & fullscreen on small screens (<768px) -->
        <div class="modal-dialog modal-dialog-scrollable" :class="modalSizeClass">
            <div class="modal-content">
                <div class="modal-header">
                    <div class="d-flex align-items-center w-100">
                        <h5 class="modal-title mb-0" :id="modalLabelId" style="flex: 1 1 auto; min-width: 0; margin-right: 1rem;" data-bs-toggle="tooltip" :title="asset ? asset.name : 'Touchpoint Information'">
                            <img v-if="asset && asset.asset_type" :src="'/img/icons/avid-icons/' + asset.asset_type.icon_class + '.svg'" class="asset-type-icon icon me-2" alt=""></img>
                            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; max-width: calc(100% - 100px); vertical-align: middle;">{{ asset ? asset.name : 'Touchpoint Information' }}</span>
                        </h5>
                        <div class="d-flex align-items-center" style="flex: 0 0 auto;">
                            <button type="button" class="btn btn-white resize-modal me-2" @click="toggleModalSize" data-bs-toggle="tooltip" :title="isLargeModal ? 'Zoom out' : 'Zoom in'">
                                <img v-if="isLargeModal" src="/img/icons/avid-icons/maximize-4.svg" class="icon" alt=""></img>
                                <img v-else src="/img/icons/avid-icons/minimize-4.svg" class="icon" alt=""></img>
                            </button>
                            <button type="button" class="btn btn-white float-end close-modal" @click="handleClose" aria-label="Close" data-bs-toggle="tooltip" title="Close">
                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                            </button>
                        </div>
                    </div>
                </div>
                <div class="modal-body" v-if="asset">
                    <!-- Name (editable) -->
                    <div class="row mb-4">
                        <div class="col-12">
                            <h6 class="text-muted mb-2">Name</h6>
                            <input
                                type="text"
                                class="form-control"
                                v-model="localName"
                                placeholder="Touchpoint name"
                                :disabled="loadingVersion" aria-label="Local Name">
                        </div>
                    </div>
                    <!-- Asset Basic Information -->
                    <div class="row mb-4">
                        <div class="col-md-6">
                            <h6 class="text-muted mb-2">Channel</h6>
                            <p class="mb-0">{{ asset.asset_type ? asset.asset_type.name : 'N/A' }}</p>
                        </div>
                        <div class="col-md-6">
                            <h6 class="text-muted mb-2">Status</h6>
                            <span v-if="status" 
                                  class="badge mx-0 ms-2" 
                                  :class="status.classes"
                                  :data-bs-toggle="status.label === 'Incomplete' ? 'tooltip' : null"
                                  :title="status.label === 'Incomplete' ? 'There are required inputs that are empty' : null">{{ status.label }}</span>
                        </div>
                    </div>

                    <!-- Audience/Segments and Version -->
                    <div class="row mb-4">
                        <div class="col-md-6" >
                            <h6 class="text-muted mb-2">Audience</h6>
                            <!-- Show segment name if segment_by_audience, otherwise show dropdown -->
                            <p v-if="isSegmentByAudience" class="mb-0">{{ asset.segment.name }}</p>
                            <select v-else class="form-select" v-model="selectedAudienceId" @change="updateAudienceSelection" :disabled="loadingVersion" aria-label="Selected Audience Id">
                                <option value="">Select an audience...</option>
                                <option v-for="audience in availableAudiences" :key="audience.id" :value="audience.id">
                                    {{ audience.name }}
                                </option>
                            </select>
                        </div>
                        <div class="col-md-6"  v-if="asset.destination_asset">
                            <h6 class="text-muted mb-2">Destination</h6>
                            <accordion-select
                                v-model="selectedDestinationOption"
                                :options="destinationOptions"
                                :if_allow_edit="!loadingVersion"
                                :splitText="false"
                                name="destination"
                                @update:modelValue="updateDestination"
                            />
                        </div>
                    </div>

                    <!-- Launch Date -->
                    <div class="row mb-4" v-if="asset.launch_date">
                        <div class="col-md-6">
                            <h6 class="text-muted mb-2">Launch Date & Time</h6>
                            <DatetimePickerLocal 
                                v-model="localLaunchDate"
                                placeholder="Select launch date and time"
                                input-class="form-control"
                                :input-format="'MM/dd/yyyy hh:mm a'"
                                :disabled="loadingVersion"
                                @update:modelValue="updateLaunchDate"
                            />
                        </div>
                        <div class="col-md-6" v-if="asset.versions && asset.versions.length > 0">
                            <h6 class="text-muted mb-2">Version</h6>
                            <select class="form-select" v-model="selectedVersionId" @change="loadVersion" :disabled="loadingVersion" aria-label="Selected Version Id">
                                <option value="">Current Version</option>
                                <option v-for="version in asset.versions" :key="version.id" :value="version.id">
                                    {{ formatVersionInfo(version) }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Description -->
                    <div class="row mb-4" v-if="asset.description">
                        <div class="col-12">
                            <h6 class="text-muted mb-2">Description</h6>
                            <p class="mb-0">{{ asset.description }}</p>
                        </div>
                    </div>

                    <!-- Asset Content Preview -->
                    <div v-if="asset.asset_type && asset.asset_type.inputs && asset.asset_type.inputs.length > 0">
                        <div v-for="inputField in getTopLevelInputs()" :key="inputField.placeholder" class="mb-3">
                            <AssetDetailsInputs 
                                :ref="'input_' + inputField.placeholder"
                                :modelValue="getInputValue(inputField.placeholder)" 
                                :input_field="createInputField(inputField)" 
                                :group_fields="getGroupFields(inputField)"
                                :client="client"
                                :asset="asset"
                                :is_generating="loadingVersion"
                                :if_allow_edit="!loadingVersion"
                                @update:modelValue="updateInputValue(inputField.placeholder, $event)" />
                        </div>
                    </div>

                    <!-- Citations Display -->
                    <div v-if="getAssetCitations().length > 0" class="citations-container mx-0 mt-3 small">
                        <img src="/img/icons/avid-icons/edna.svg" class="icon me-1" alt=""></img>
                        This touchpoint was developed using the following sources:
                        <template v-for="(citation, idx) in getAssetCitations()" :key="citation.id">
                            <template v-if="idx < getAssetCitations().length - 2">
                                <a :href="getCitationLink(citation)" target="_blank">{{ citation.name }}</a><span>, </span>
                            </template>
                            <template v-else-if="idx == getAssetCitations().length - 2">
                                <a :href="getCitationLink(citation)" target="_blank">{{ citation.name }}</a><span> and </span>
                            </template>
                            <template v-else>
                                <a :href="getCitationLink(citation)" target="_blank">{{ citation.name }}</a>
                            </template>
                        </template>
                    </div>
                </div>
                <div class="modal-footer d-flex justify-content-end gap-2 pt-1">
                    <RegenerateButton 
                        v-if="asset"
                        :asset="asset" 
                        :client="client" 
                        :concepts="concepts"
                        @asset-generating="handleAssetGenerating"
                        @asset-generated="handleAssetGenerated"
                    />
                    <button v-if="canEdit('playbooks')" type="button" class="btn btn-primary" @click="handleEditAsset">Save</button>
                    <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <button type="button" class="btn btn-primary" :disabled="true">Save</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../mixins/permissions';
import { useNotifications } from '../../composables/useNotifications';
import AssetDetailsInputs from './AssetDetailsInputs.vue';
import RegenerateButton from './RegenerateButton.vue';
import AccordionSelect from '../components/AccordionSelect.vue';
import DatepickerUtc from '../components/DatepickerUtc.vue';
import { usePlaybookStore } from '../../stores/playbook';

export default defineComponent({
    name: 'EditTouchpointModal',
    mixins: [permissionsMixin],
    components: {
        AssetDetailsInputs,
        RegenerateButton,
        DatepickerUtc,
        AccordionSelect
    },
    setup() {
        const playbookStore = usePlaybookStore();
        const { showNotification } = useNotifications();
        return {
            playbookStore,
            showNotification
        };
    },
    props: {
        asset: {
            type: Object,
            default: null
        },
        show: {
            type: Boolean,
            default: false
        },
        client: {
            type: Object,
            default: () => ({})
        },
        concepts: {
            type: Array,
            default: () => []
        },
        status: {
            type: Object,
            default: null
        }
    },
    emits: ['edit-asset', 'close', 'asset-generated'],
    data() {
        return {
            updatedInputs: {},
            localName: '',
            localLaunchDate: null,
            isLargeModal: true, // Start with modal-lg (true = large, false = extra large)
            selectedVersionId: '',
            currentVersionData: null,
            selectedAudienceId: '',
            selectedDestinationOption: null,
            loadingVersion: false
        };
    },
    computed: {
        modalId() {
            return `assetInfoModal-${this.asset?.id || 'new'}`;
        },
        modalLabelId() {
            return `assetInfoModalLabel-${this.asset?.id || 'new'}`;
        },
        modalSizeClass() {
            return this.isLargeModal ? 'modal-lg' : 'modal-xl';
        },
        isSegmentByAudience() {
            return this.asset?.playbook_type_asset_type?.pivot?.segment_by_audience === true;
        },
        availableAudiences() {
            return this.playbookStore.getAvailableAudiences || [];
        },
        destinationOptions() {
            const playbook = this.playbookStore.getSelectedPlaybook;
            if(!playbook || !playbook.assets) return [];
            return playbook.assets.map(a=>({
                value:a.id,
                text: a.name || 'Untitled Touchpoint',
                category:''
            }));
        },
        isAssetGenerating() {
            return this.asset?.status === 'generating';
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.showModal();
                });
            } else {
                this.hideModal();
            }
        },
        asset: {
            immediate: true,
            handler(newAsset) {
                // Reset updatedInputs when switching to a different asset
                this.updatedInputs = {};
                
                // Initialize local name
                if (newAsset && newAsset.name) {
                    this.localName = newAsset.name;
                } else {
                    this.localName = '';
                }
                
                // Ensure inputs is always an array (for newly created assets)
                if (newAsset && (!newAsset.inputs || !Array.isArray(newAsset.inputs))) {
                    newAsset.inputs = [];
                }
                
                if (newAsset && newAsset.launch_date) {
                    this.localLaunchDate = new Date(newAsset.launch_date);
                } else {
                    this.localLaunchDate = null;
                }

                // init destination option
                if(newAsset && newAsset.destination_asset){
                    this.selectedDestinationOption = {
                        value:newAsset.destination_asset.id,
                        text:newAsset.destination_asset.name,
                        category:''
                    };
                } else {
                    this.selectedDestinationOption = null;
                }
                
                // Initialize selectedAudienceId if asset has a segment and it's not segment_by_audience
                if (newAsset && newAsset.segment && !this.isSegmentByAudience) {
                    this.selectedAudienceId = newAsset.segment.id;
                } else {
                    this.selectedAudienceId = '';
                }
            }
        }
    },
    methods: {

        showModal() {
            const modalElement = document.getElementById(this.modalId);
            if (modalElement) {
                // Dispose any existing modal instance first
                const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                if (existingModal) {
                    existingModal.dispose();
                }
                
                const modal = new window.bootstrap.Modal(modalElement);
                modal.show();
            }
        },

        handleClose() {
            this.hideModal();
        },

        hideModal() {
            const modalElement = document.getElementById(this.modalId);
            if (!modalElement) {
                return;
            }
            
            // Check if element is still in the DOM
            if (!document.body.contains(modalElement)) {
                return;
            }
            
            try {
                const modal = window.bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                    // Check if modal is already hidden or in the process of hiding
                    if (modalElement.classList.contains('show') || modalElement.classList.contains('is-generating')) {
                        modal.hide();
                    }
                }
            } catch (error) {
                // Silently handle errors during hide (element may have been removed)
                console.warn('Error hiding modal:', error);
            }
        },

        updateInputValue(placeholder, value) {
            this.updatedInputs[placeholder] = value;
        },

        updateLaunchDate(newDate) {
            this.updatedInputs.launch_date = newDate;
        },

        updateAudienceSelection() {
            if (this.selectedAudienceId) {
                // Find the selected audience from available audiences
                const selectedAudience = this.availableAudiences.find(audience => audience.id === parseInt(this.selectedAudienceId));
                if (selectedAudience) {
                    this.updatedInputs.segment = {
                        id: selectedAudience.id,
                        name: selectedAudience.name,
                        type: 'App\\Models\\Segment'
                    };
                }
            } else {
                // Clear segment selection
                this.updatedInputs.segment = null;
            }
        },

        updateDestination(option){
            if(option){
                this.updatedInputs.destination_asset_id = option.value;
            } else {
                this.updatedInputs.destination_asset_id = null;
            }
        },

        handleEditAsset() {

            // Capture current values from all AssetDetailsInputs components
            this.captureAllInputValues();
            
            // Ensure audience selection is captured (in case change event didn't fire)
            if (!this.isSegmentByAudience && this.selectedAudienceId) {
                const selectedAudience = this.availableAudiences.find(audience => audience.id === parseInt(this.selectedAudienceId));
                if (selectedAudience) {
                    this.updatedInputs.segment = {
                        id: selectedAudience.id,
                        name: selectedAudience.name,
                        type: 'App\\Models\\Segment'
                    };
                }
            } else if (!this.isSegmentByAudience && !this.selectedAudienceId) {
                // Clear segment if no audience is selected
                this.updatedInputs.segment = null;
            }
            
            // Create updated asset with new input values
            const updatedAsset = { ...this.asset };
            
            // Update name if changed
            if (this.localName !== undefined && this.localName !== null && String(this.localName).trim() !== '') {
                updatedAsset.name = String(this.localName).trim();
            }
            
            // Update launch date if changed
            if (this.updatedInputs.hasOwnProperty('launch_date')) {
                updatedAsset.launch_date = this.updatedInputs.launch_date;
            }
            
            // Update segment if changed (only for non-segment_by_audience assets)
            if (this.updatedInputs.hasOwnProperty('segment') && !this.isSegmentByAudience) {
                updatedAsset.segment = this.updatedInputs.segment;
            }
            
            // Destination
            if(this.updatedInputs.hasOwnProperty('destination_asset_id')){
                updatedAsset.destination_asset_id = this.updatedInputs.destination_asset_id;
            }

            // Update inputs with any changes made
            // Initialize inputs array if it doesn't exist
            if (!updatedAsset.inputs || !Array.isArray(updatedAsset.inputs)) {
                updatedAsset.inputs = [];
            }
            
            // Get existing inputs as a map for easier lookup
            const existingInputsMap = {};
            if (this.asset.inputs && Array.isArray(this.asset.inputs)) {
                this.asset.inputs.forEach(input => {
                    existingInputsMap[input.placeholder] = input;
                });
            }
            
            // Process all captured input values
            Object.keys(this.updatedInputs).forEach(placeholder => {
                // Skip non-input fields
                if (placeholder === 'launch_date' || placeholder === 'name' || placeholder === 'segment' || placeholder === 'destination_asset_id') {
                    return;
                }
                
                // Check if this input already exists in the asset
                if (existingInputsMap[placeholder]) {
                    // Update existing input
                    const existingIndex = updatedAsset.inputs.findIndex(input => input.placeholder === placeholder);
                    if (existingIndex >= 0) {
                        updatedAsset.inputs[existingIndex] = {
                            ...existingInputsMap[placeholder],
                            value: this.updatedInputs[placeholder]
                        };
                    } else {
                        updatedAsset.inputs.push({
                            ...existingInputsMap[placeholder],
                            value: this.updatedInputs[placeholder]
                        });
                    }
                } else {
                    // Add new input - find the input field definition from asset_type
                    const inputFieldDef = this.asset.asset_type.inputs.find(field => field.placeholder === placeholder);
                    if (inputFieldDef) {
                        updatedAsset.inputs.push({
                            placeholder: placeholder,
                            value: this.updatedInputs[placeholder],
                            name: inputFieldDef.name,
                            type: inputFieldDef.type
                        });
                    }
                }
            });
            
            // Also include any existing inputs that weren't updated (to preserve them)
            // This ensures we don't lose any inputs that weren't modified
            if (this.asset.inputs && Array.isArray(this.asset.inputs) && this.asset.inputs.length > 0) {
                this.asset.inputs.forEach(input => {
                    if (!this.updatedInputs.hasOwnProperty(input.placeholder)) {
                        // Only add if it's not already in updatedAsset.inputs
                        const exists = updatedAsset.inputs.some(updatedInput => updatedInput.placeholder === input.placeholder);
                        if (!exists) {
                            updatedAsset.inputs.push(input);
                        }
                    }
                });
            }
            
            // For newly created assets, if no inputs were captured but asset_type has input definitions,
            // ensure we still send an empty inputs array (not null)
            if (updatedAsset.inputs.length === 0 && this.asset.asset_type && this.asset.asset_type.inputs) {
                // Keep it as an empty array - this is correct for new assets
                // The backend will initialize inputs based on asset_type.inputs if needed
            }
            
            this.$emit('edit-asset', updatedAsset);
            this.hideModal();
        },

        captureAllInputValues() {
            // Capture values from all AssetDetailsInputs component refs
            // Only iterate through top-level inputs (those without group_id)
            // Group inputs will contain all their child field values
            if (this.asset?.asset_type?.inputs) {
                const topLevelInputs = this.getTopLevelInputs();
                
                topLevelInputs.forEach(inputField => {
                    const refKey = 'input_' + inputField.placeholder;
                    const componentRef = this.$refs[refKey];

                    // ComponentRef might be an array if there are multiple with the same ref
                    const component = Array.isArray(componentRef) ? componentRef[0] : componentRef;

                    if (component && component.local_value !== undefined) {
                        // Capture the current local_value from the component
                        this.updatedInputs[inputField.placeholder] = component.local_value;
                    }
                });
            }
        },

        handleAssetGenerated(asset) {
            this.isGenerating = false;
            this.$emit('edit-asset', asset);
        },

        handleAssetGenerating(asset) {
            this.hideModal();
            // Keep the modal open but immediately reflect the generating state
            if (asset?.id) {
                // Mutate local reference so computed class updates instantly
                asset.status = 'generating';
                // Sync the store so other components see the state right away
                this.playbookStore.updateAsset(asset.id, { status: 'generating' });
            }
        },

        createInputField(inputField) {
            // Transform the input field definition to match AssetDetailsInputs expected format
            return {
                id: inputField.id,
                name: inputField.name || inputField.placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                description: inputField.description || inputField.placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                placeholder: inputField.placeholder,
                type: inputField.type,
                optional: inputField.optional,
                max_length: inputField.max_length,
                repeatable: inputField.repeatable,
                options: inputField.options || [],
                group_id: inputField.group_id
            };
        },

        getInputValue(placeholder) {
            // First check if we have updated inputs (from version selection or manual edits)
            if (this.updatedInputs.hasOwnProperty(placeholder)) {
                return this.updatedInputs[placeholder];
            }

            // Find the corresponding value from asset.inputs array
            if (!this.asset.inputs || !Array.isArray(this.asset.inputs)) {
                return this.getDefaultValue();
            }
            
            const inputData = this.asset.inputs.find(input => input.placeholder === placeholder);
            return inputData ? inputData.value : this.getDefaultValue();
        },

        getDefaultValue() {
            // Return appropriate default value based on input type
            return '';
        },

        getTopLevelInputs() {
            // Return only inputs that don't belong to a group (group_id is null/undefined)
            if (!this.asset.asset_type || !this.asset.asset_type.inputs) {
                return [];
            }
            return this.asset.asset_type.inputs.filter(input => !input.group_id);
        },

        getGroupFields(inputField) {
            // If this is a group, return all fields that belong to it
            if (inputField.type === 'group' && this.asset.asset_type && this.asset.asset_type.inputs) {
                return this.asset.asset_type.inputs.filter(input => input.group_id === inputField.id);
            }
            return [];
        },


        setupModalEventListeners() {
            const modalElement = document.getElementById(this.modalId);
            if (!modalElement) {
                return;
            }
            
            // Listen for modal close events
            const handleHidden = () => {
                this.$emit('close');
            };
            modalElement.addEventListener('hidden.bs.modal', handleHidden);
            
            // Dispose tooltips when modal is hiding (before Bootstrap tries to clean up)
            const handleHide = () => {
                try {
                    const tooltipTriggerList = document.querySelectorAll(`#${this.modalId} [data-bs-toggle="tooltip"]`);
                    [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                        if (tooltipTriggerEl && document.body.contains(tooltipTriggerEl)) {
                            const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                            if (existingTooltip) {
                                existingTooltip.dispose();
                            }
                        }
                    });
                } catch (error) {
                    // Silently handle errors during tooltip cleanup
                    console.warn('Error disposing tooltips:', error);
                }
            };
            modalElement.addEventListener('hide.bs.modal', handleHide);
            
            // Initialize tooltips when modal is shown
            const handleShown = () => {
                this.$nextTick(() => {
                    try {
                        const tooltipTriggerList = document.querySelectorAll(`#${this.modalId} [data-bs-toggle="tooltip"]`);
                        [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                            if (tooltipTriggerEl && document.body.contains(tooltipTriggerEl)) {
                                // Dispose existing tooltip if any
                                const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                                if (existingTooltip) {
                                    existingTooltip.dispose();
                                }
                                // Create new tooltip
                                new window.bootstrap.Tooltip(tooltipTriggerEl);
                            }
                        });
                    } catch (error) {
                        console.warn('Error initializing tooltips:', error);
                    }
                });
            };
            modalElement.addEventListener('shown.bs.modal', handleShown);
        },

        toggleModalSize() {
            this.isLargeModal = !this.isLargeModal;
        },

        formatVersionInfo(version) {
            const date = new Date(version.created_at);
            // Browser locale; include date & time
            const localStr = date.toLocaleString(undefined, {
                year: 'numeric', month: 'short', day: '2-digit',
                hour: 'numeric', minute: '2-digit'
            });
            const byUser = version.updated_by_user ? (version.updated_by_user.name || 'Unknown') : 'Unknown';
            return `${localStr} - ${byUser}`;
        },

        async loadVersion() {
            if (!this.selectedVersionId) {
                // Reset to current version
                this.currentVersionData = null;
                this.updatedInputs = {};
                this.loadingVersion = false;
                return;
            }

            // Find the selected version (for display info)
            const selectedVersion = this.asset.versions.find(v => v.id == this.selectedVersionId);
            if (selectedVersion) {
                this.currentVersionData = selectedVersion;
                this.loadingVersion = true;
                
                // Fetch inputs from API since they're not loaded with versions anymore
                try {
                    const response = await window.axios.get(
                        `/api/playbooks/${this.client.url}/assets/${this.asset.id}/versions/${this.selectedVersionId}/inputs`
                    );
                    
                    // Update inputs with version data
                    if (response.data.inputs && Array.isArray(response.data.inputs)) {
                        response.data.inputs.forEach(input => {
                            this.updatedInputs[input.placeholder] = input.value;
                        });
                    }
                    
                    // Update launch date if it exists in the version
                    if (response.data.launch_date) {
                        this.localLaunchDate = new Date(response.data.launch_date);
                        this.updatedInputs.launch_date = response.data.launch_date;
                    }
                } catch (error) {
                    console.error('Error loading version inputs:', error);
                    // Show error notification
                    this.showNotification({
                        icon: '/img/icons/avid-icons/danger.svg',
                        text: 'Failed to load version inputs'
                    });
                } finally {
                    this.loadingVersion = false;
                }
            }
        },

        getAssetCitations() {
            if (!this.asset || !this.asset.inputs || !Array.isArray(this.asset.inputs)) {
                return [];
            }

            // Find the citations input
            const citationsInput = this.asset.inputs.find(input => input.placeholder === 'citations');
            if (!citationsInput || !citationsInput.value) {
                return [];
            }

            let citations = citationsInput.value;
            // If citations is an object, make it into an array
            if (typeof citations === 'object' && !Array.isArray(citations)) {
                citations = Object.values(citations);
            }

            // Remove the 'Brand Information' citations (same as PlayStrategyView)
            citations = citations.filter(citation => citation.name !== 'Brand Information');
            
            return citations;
        },

        getCitationLink(citation) {
            if (citation.source_url) {
                return citation.source_url;
            } else {
                return '/internal/' + this.client.url + '/redirect/' + citation.id;
            }
        }
    },
    mounted() {
        this.setupModalEventListeners();
    },
    // updated() {
    //     this.setupModalEventListeners();
    // }
});
</script>
